import { TMessageHandler, TMessage, IBaseCommunicator } from './types';

/**
 * postMessage 通信的基类
 */
export default abstract class BaseCommunicator implements IBaseCommunicator {
  protected messageHandlers: Map<string, TMessageHandler> = new Map();
  protected events: Map<string, Set<Function>> = new Map();
  protected allowedOrigins: string[] = [];

  protected constructor() {
    // 构建类时, 直接设置message监听
    this.setupMessageListener();
  }

  // 添加消息处理
  public addMessageHandler(type: string, handler: TMessageHandler): void {
    this.messageHandlers.set(type, handler);
  }

  // 添加事件
  public on(event: string, callback: Function): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)?.add(callback);
  }

  // 移除事件
  public off(event: string, callback: Function): void {
    this.events.get(event)?.delete(callback);
  }

  // 触发事件
  public emit(event: string, ...args: any[]): void {
    this.events.get(event)?.forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in ${event} listener:`, error);
      }
    });
  }

  // 清理
  public destroy(): void {
    window.removeEventListener('message', this.handleMessage.bind(this));
    this.messageHandlers.clear();
    this.events.clear();
  }
  // 检查token有效期
  public checkTokenExpiry(token: string, minutesBeforeExpire = 0): boolean {
    if (!token) return false;
    try {

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // 转换为毫秒
      const now = Date.now();
      const threshold = minutesBeforeExpire * 60 * 1000;

      if (now + threshold >= expiry) {
        console.warn(`token将会在 ${minutesBeforeExpire} 分钟内过期`);
        return false;
      }

      return true;
    } catch (error) {
      console.error('解析Token错误:', error);
      return false;
    }
  }

  // 配置 允许 message 的来源
  public configAllowedOrigins(allowedOrigins: string[]): void {
    allowedOrigins.forEach(origin => this.allowedOrigins.push(origin));
  }

  // 验证发送消息的来源
  public validateSendMessageOrigin(origin: string): boolean {
    // 如果没有设置允许的源，则直接通过
    if (!this.allowedOrigins || this.allowedOrigins.length === 0) {
      return true;
    }

    // 检查origin是否在允许列表中
    return this.allowedOrigins.includes(origin);
  };
  // window的message事件的处理方法
  protected handleMessage(event: MessageEvent) {
    // 先校验接收到的消息来源是否安全
    if (!this.validateSendMessageOrigin(event.origin)) {
      console.error(`不安全来源: ${event.origin}`);
      return;
    }
    // 对消息进行处理
    const message = event.data as TMessage;
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      try {
        handler(message.payload, event);
      } catch (error) {
        console.error(`Error handling message ${message.type}:`, error);
      }
    }
  }

  // 设置message事件
  private setupMessageListener(): void {
    window.addEventListener('message', this.handleMessage.bind(this));
  }
}
